import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { TrendingUp } from "@mui/icons-material";
import { MenuItem, Stack, TextField, useTheme } from "@mui/material";
import { friendlyDate, friendlyMonth } from "Helpers/dates";
import { formatNumber } from "Helpers/numbers";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import AnalyticsCardPanelHeader from "features/analytics/components/AnalyticsCardPanelHeader";
import StyledComposedChart from "features/analytics/components/StyledComposedChart";
import useChartData from "features/analytics/hooks/useChartData";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import getActiveCustomersTrend from "features/analytics/services/getActiveCustomersTrend";
import { formatPercentAxisTick } from "features/analytics/services/formatters/rateFormatter";
import { formatDimension } from "features/analytics/services/dimensionsFormatter";
import { DIMENSION_TRUNCATE } from "features/analytics/constants";
import { truncate } from "Helpers/truncate";
import { TChartData } from "Interfaces";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import AnalyticsCard from "./AnalyticsCard";
import AnalyticsChartArea from "Components/AnalyticsChartArea";
import { useCustomerRetentionCohort } from "..";

const ACTIVE_CUSTOMERS_FIELD = "active_customers";
const PROJECT_START_LABEL = "Incendium Start";

const GRANULARITY_OPTIONS = [
  {
    value: ApiDimension.DIMENSION_SESSION_START_BY_DAY,
    label: "Daily",
  },
  {
    value: ApiDimension.DIMENSION_SESSION_START_BY_WEEK,
    label: "Weekly",
  },
  {
    value: ApiDimension.DIMENSION_SESSION_START_BY_MONTH,
    label: "Monthly",
  },
] as const;

const RETENTION_WINDOW_OPTIONS = [90, 60, 30] as const;

function tickKey(
  date: string | Date | moment.Moment,
  granularity: ApiDimension,
): string {
  const m = moment(date);
  switch (granularity) {
    case ApiDimension.DIMENSION_SESSION_START_BY_MONTH:
      return m.clone().startOf("month").format("YYYY-MM-DD");
    case ApiDimension.DIMENSION_SESSION_START_BY_WEEK:
      return m.clone().startOf("isoWeek").format("YYYY-MM-DD");
    default:
      return m.clone().startOf("day").format("YYYY-MM-DD");
  }
}

function formatTickLabel(
  date: string | Date | moment.Moment,
  granularity: ApiDimension,
): string {
  switch (granularity) {
    case ApiDimension.DIMENSION_SESSION_START_BY_MONTH:
      return friendlyMonth(date);
    case ApiDimension.DIMENSION_SESSION_START_BY_WEEK:
      return formatDimension(
        ApiDimension.DIMENSION_SESSION_START_BY_WEEK,
        friendlyDate(date),
      );
    default:
      return friendlyDate(date);
  }
}

interface IRetentionMetricsOverTimeProps {
  staticRetentionWindowDays?: number;
  staticGranularity?: ApiDimension;
  attributes?: ApiChartAttribute[];
}

function RetentionMetricsOverTime({
  staticRetentionWindowDays,
  staticGranularity,
  attributes = [],
}: IRetentionMetricsOverTimeProps) {
  const theme = useTheme();
  const metricToName = useMetricName();
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  const [granularity, setGranularity] = useState<ApiDimension>(
    staticGranularity || ApiDimension.DIMENSION_SESSION_START_BY_DAY,
  );
  const [retentionWindowDays, setRetentionWindowDays] = useState<number>(
    staticRetentionWindowDays || 90,
  );
  const [activeCustomersPoints, setActiveCustomersPoints] = useState<
    { date: Date; count: number }[]
  >([]);
  const [activeCustomersLoading, setActiveCustomersLoading] = useState(false);
  const { dateDimension } = useCustomerRetentionCohort();

  const chart = useMemo(
    () => ({
      name: "Key Retention Metrics Over Time",
      dimension: [granularity],
      template: ApiChartTemplate.TREND,
      type: ApiChartType.GRAPH,
      attributes,
      dateDimension,
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_REPEAT_CUSTOMER_RATE,
            ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
          ],
        },
      ],
    }),
    [granularity, dateDimension, attributes],
  );

  const { chartData, loading: ratesLoading } = useChartData(
    selectedProject,
    chart,
    chartOutput,
  );

  useEffect(() => {
    if (!selectedProject?.id || !chartOutput) {
      setActiveCustomersPoints([]);
      return;
    }

    let cancelled = false;
    setActiveCustomersLoading(true);

    getActiveCustomersTrend(
      selectedProject.id,
      chartOutput.from ? new Date(chartOutput.from) : undefined,
      chartOutput.to ? new Date(chartOutput.to) : undefined,
      chartOutput.lastNDays,
      granularity,
      retentionWindowDays,
    )
      .then((res) => {
        if (cancelled) {
          return;
        }
        setActiveCustomersPoints(
          (res.points || []).map((point) => ({
            date: point.date ? new Date(point.date) : new Date(),
            count: point.count || 0,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) {
          setActiveCustomersLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id, chartOutput, granularity, retentionWindowDays]);

  const activeCustomersLabel = `${retentionWindowDays} Day Active Customers`;

  const mergedChartData = useMemo((): TChartData[] => {
    const rows = new Map<string, TChartData>();

    (chartData?.data || []).forEach((row) => {
      const key = tickKey(row.name, granularity);
      rows.set(key, {
        ...row,
        name: formatTickLabel(key, granularity),
      });
    });

    activeCustomersPoints.forEach((point) => {
      const key = tickKey(point.date, granularity);
      const existing = rows.get(key) || {
        name: formatTickLabel(key, granularity),
      };
      rows.set(key, {
        ...existing,
        [ACTIVE_CUSTOMERS_FIELD]: point.count,
      });
    });

    return Array.from(rows.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, row]) => row);
  }, [activeCustomersPoints, chartData?.data, granularity]);

  const projectStartLine = useMemo(() => {
    if (!selectedProject?.createdAt) {
      return [];
    }
    const launchTick = tickKey(selectedProject.createdAt, granularity);
    return [
      {
        x: formatTickLabel(launchTick, granularity),
        label: PROJECT_START_LABEL,
        stroke: theme.palette.primary.main,
      },
    ];
  }, [granularity, selectedProject?.createdAt, theme.palette.primary.main]);

  const metricsOrder = useMemo(
    () => [
      ApiMetric.METRIC_REPEAT_CUSTOMER_RATE,
      ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
      ACTIVE_CUSTOMERS_FIELD,
    ],
    [],
  );

  const renderTooltipValue = useCallback(
    (key: string, value: number | string) => {
      const name =
        key === ACTIVE_CUSTOMERS_FIELD
          ? activeCustomersLabel
          : metricToName(key as ApiMetric);

      if (key === ACTIVE_CUSTOMERS_FIELD) {
        return `${name}: ${formatNumber(Number(value))}`;
      }
      if (
        key === ApiMetric.METRIC_REPEAT_CUSTOMER_RATE ||
        key === ApiMetric.METRIC_RETURNING_CUSTOMER_RATE
      ) {
        return `${name}: ${formatPercentAxisTick(value)}`;
      }
      return `${name}: ${value}`;
    },
    [activeCustomersLabel, metricToName],
  );

  const loading = ratesLoading || activeCustomersLoading;

  return (
    <AnalyticsCard
      noToolbar
      chart={chart}
      customHeader={
        <AnalyticsCardPanelHeader
          icon={<TrendingUp fontSize="small" />}
          title="Key Retention Metrics Over Time"
          subtitle="How repeat customer and purchase behaviour changes over time"
          action={
            !staticRetentionWindowDays || !staticGranularity ? (
              <Stack direction="row" spacing={1}>
                {!staticRetentionWindowDays && (
                  <TextField
                    select
                    size="small"
                    label="Retention Window"
                    sx={{ minWidth: 110 }}
                    value={retentionWindowDays}
                    onChange={(e) =>
                      setRetentionWindowDays(Number(e.target.value))
                    }
                  >
                    {RETENTION_WINDOW_OPTIONS.map((days) => (
                      <MenuItem key={days} value={days}>
                        {days} days
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {!staticGranularity && (
                  <TextField
                    select
                    size="small"
                    label="Granularity"
                    sx={{ minWidth: 130 }}
                    value={granularity}
                    onChange={(e) =>
                      setGranularity(e.target.value as ApiDimension)
                    }
                  >
                    {GRANULARITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            ) : undefined
          }
        />
      }
      body={
        <AnalyticsChartArea
          loading={loading && mergedChartData.length === 0}
          empty={
            !loading && mergedChartData.length === 0
              ? "No data for this period"
              : undefined
          }
          graphLayout
        >
          <StyledComposedChart
            data={mergedChartData}
            showTooltip
            noReOrder
            metricsOrder={metricsOrder}
            verticalReferenceLines={projectStartLine}
            dimensions={[granularity]}
            xAxisProps={{
              tickFormatter: (value) =>
                truncate(`${value}`, DIMENSION_TRUNCATE),
            }}
            tooltipProps={{
              renderValueFn: renderTooltipValue,
            }}
            yAxisKeys={[
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_REPEAT_CUSTOMER_RATE,
                  ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
                ],
                chart: {
                  [ApiMetric.METRIC_REPEAT_CUSTOMER_RATE]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_RETURNING_CUSTOMER_RATE]:
                    ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ACTIVE_CUSTOMERS_FIELD],
                chart: {
                  [ACTIVE_CUSTOMERS_FIELD]: ApiYAxisChartType.LINE,
                },
              },
            ]}
            yAxisProps={[
              {
                tickFormatter: formatPercentAxisTick,
              },
              {
                tickFormatter: (value) => formatNumber(value) as string,
              },
            ]}
          />
        </AnalyticsChartArea>
      }
    />
  );
}

export default RetentionMetricsOverTime;
